import { supabase } from "@/integrations/supabase/client";
import { realtimeChannelsCountDelta } from "@/lib/frontend-telemetry";
import { noteConversationChangeForChannelCounts } from "@/lib/inbox-channel-counts";
import { INBOX_HIDDEN_GRACE_MS } from "@/lib/inbox-live-sync";
import type { RealtimeChannel, RealtimePostgresChangesPayload } from "@supabase/supabase-js";

export type ConversationChangeHandler = (
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
) => void;

export const REALTIME_PAUSE_ON_HIDDEN_ENABLED =
  import.meta.env.VITE_REALTIME_PAUSE_ON_HIDDEN !== "0";

type HubEntry = {
  channel: RealtimeChannel | null;
  handlers: Set<ConversationChangeHandler>;
  workspaceId: string;
  backoffMs: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  /** Timestamp do último evento postgres_changes recebido neste workspace. */
  lastEventAt: number | null;
  /** Canal entrou em SUBSCRIBED. Cobre inbox quieta, sem evento ainda. */
  subscribedAt: number | null;
  /** Canal esteve fora de `joined`; ao reassinar, dispara reconcile. */
  wasDown: boolean;
};

const hubs = new Map<string, HubEntry>();

const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const WATCHDOG_INTERVAL_MS = 30000;

export type HubHealthHandle = {
  /** Reassina imediatamente (watchdog). Ignora se já estiver saudável. */
  ensureHealthy: () => void;
  isHealthy: () => boolean;
};

let hubPaused = false;
let hidePauseTimer: ReturnType<typeof setTimeout> | null = null;
let watchdogInstalled = false;
let visibilityListenerInstalled = false;
const reconcileListeners = new Set<() => void>();

function isHubEffectivelyPaused(): boolean {
  return REALTIME_PAUSE_ON_HIDDEN_ENABLED && hubPaused;
}

function dispatchHubReconcile() {
  for (const listener of reconcileListeners) {
    try {
      listener();
    } catch (err) {
      console.error("workspace conversation hub reconcile listener:", err);
    }
  }
}

export function onHubReconcile(callback: () => void): () => void {
  reconcileListeners.add(callback);
  return () => {
    reconcileListeners.delete(callback);
  };
}

function pauseAllHubs() {
  if (!REALTIME_PAUSE_ON_HIDDEN_ENABLED || hubPaused) return;
  hubPaused = true;

  for (const entry of hubs.values()) {
    if (entry.reconnectTimer) {
      clearTimeout(entry.reconnectTimer);
      entry.reconnectTimer = null;
    }
    teardownChannel(entry);
  }
}

function resumeAllHubs() {
  if (!REALTIME_PAUSE_ON_HIDDEN_ENABLED || !hubPaused) return;
  hubPaused = false;

  for (const entry of hubs.values()) {
    if (entry.handlers.size > 0) {
      entry.backoffMs = MIN_BACKOFF_MS;
      subscribeHub(entry);
    }
  }

  dispatchHubReconcile();
}

function syncPauseFromVisibility() {
  if (!REALTIME_PAUSE_ON_HIDDEN_ENABLED || typeof document === "undefined") return;
  if (document.hidden) {
    if (hubPaused || hidePauseTimer) return;
    hidePauseTimer = setTimeout(() => {
      hidePauseTimer = null;
      if (typeof document !== "undefined" && !document.hidden) return;
      pauseAllHubs();
    }, INBOX_HIDDEN_GRACE_MS);
    return;
  }

  if (hidePauseTimer) {
    clearTimeout(hidePauseTimer);
    hidePauseTimer = null;
    return;
  }

  resumeAllHubs();
}

function isChannelHealthy(channel: RealtimeChannel | null): boolean {
  return Boolean(channel && channel.state === "joined");
}

function installVisibilityListener() {
  if (visibilityListenerInstalled || typeof document === "undefined") return;
  visibilityListenerInstalled = true;
  document.addEventListener("visibilitychange", syncPauseFromVisibility);
  // Sincroniza estado inicial nos dois sentidos (evita hubPaused=true com aba visível).
  syncPauseFromVisibility();
}

function ensureHub(workspaceId: string): HubEntry {
  let entry = hubs.get(workspaceId);
  if (!entry) {
    entry = {
      channel: null,
      handlers: new Set(),
      workspaceId,
      backoffMs: MIN_BACKOFF_MS,
      reconnectTimer: null,
      lastEventAt: null,
      subscribedAt: null,
      wasDown: false,
    };
    hubs.set(workspaceId, entry);
  }
  return entry;
}

function teardownChannel(entry: HubEntry) {
  if (!entry.channel) return;
  supabase.removeChannel(entry.channel);
  entry.channel = null;
  realtimeChannelsCountDelta(-1);
}

function scheduleReconnect(entry: HubEntry, immediate = false) {
  if (entry.reconnectTimer || entry.handlers.size === 0 || isHubEffectivelyPaused()) return;

  entry.wasDown = true;
  const jitter = immediate ? 0 : Math.floor(Math.random() * 300);
  const delay = immediate ? 0 : entry.backoffMs + jitter;
  entry.backoffMs = Math.min(entry.backoffMs * 2, MAX_BACKOFF_MS);
  entry.reconnectTimer = setTimeout(() => {
    entry.reconnectTimer = null;
    teardownChannel(entry);
    if (entry.handlers.size > 0 && !isHubEffectivelyPaused()) {
      subscribeHub(entry);
    }
  }, delay);
}

function ensureHubHealthy(entry: HubEntry) {
  if (entry.handlers.size === 0 || isHubEffectivelyPaused()) return;
  if (isChannelHealthy(entry.channel)) return;
  if (entry.reconnectTimer) return;
  scheduleReconnect(entry, true);
}

export function ensureWorkspaceConversationHubHealthy(workspaceId: string): void {
  const entry = hubs.get(workspaceId);
  if (entry) ensureHubHealthy(entry);
}

export function ensureAllWorkspaceConversationHubsHealthy(): void {
  for (const entry of hubs.values()) {
    ensureHubHealthy(entry);
  }
}

export type WorkspaceHubHealth = {
  /** Canal atualmente `joined`. */
  joined: boolean;
  /** Timestamp (ms) do último evento recebido; null se nunca chegou. */
  lastEventAt: number | null;
  /** Timestamp (ms) da última assinatura confirmada. */
  subscribedAt: number | null;
  /** Canal caiu em algum momento e ainda não confirmou reassinatura. */
  wasDown: boolean;
};

/** Sinal de saúde do hub por workspace — usado pelo fallback de staleness da inbox. */
export function getWorkspaceConversationHubHealth(workspaceId: string): WorkspaceHubHealth {
  const entry = hubs.get(workspaceId);
  if (!entry) return { joined: false, lastEventAt: null, subscribedAt: null, wasDown: false };
  return {
    joined: isChannelHealthy(entry.channel),
    lastEventAt: entry.lastEventAt,
    subscribedAt: entry.subscribedAt,
    wasDown: entry.wasDown,
  };
}

function installWatchdog() {
  if (watchdogInstalled || typeof window === "undefined") return;
  watchdogInstalled = true;
  window.setInterval(() => {
    if (typeof document !== "undefined" && document.hidden) return;
    ensureAllWorkspaceConversationHubsHealthy();
  }, WATCHDOG_INTERVAL_MS);
}

function subscribeHub(entry: HubEntry) {
  if (isHubEffectivelyPaused()) return;
  if (isChannelHealthy(entry.channel)) return;
  if (entry.channel) {
    teardownChannel(entry);
  }

  const channel = supabase
    .channel(`workspace-conversations-${entry.workspaceId}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "conversations",
        filter: `workspace_id=eq.${entry.workspaceId}`,
      },
      (payload) => {
        entry.lastEventAt = Date.now();
        noteConversationChangeForChannelCounts(
          entry.workspaceId,
          payload as { eventType?: string; old?: Record<string, unknown>; new?: Record<string, unknown> },
        );
        for (const handler of entry.handlers) {
          try {
            handler(payload as RealtimePostgresChangesPayload<Record<string, unknown>>);
          } catch (err) {
            console.error("workspace conversation realtime handler:", err);
          }
        }
      },
    )
    .subscribe((status) => {
      if (status === "SUBSCRIBED") {
        entry.backoffMs = MIN_BACKOFF_MS;
        entry.subscribedAt = Date.now();
        if (entry.wasDown) {
          // Recuperou de queda: eventos podem ter sido perdidos — reconcilia.
          entry.wasDown = false;
          dispatchHubReconcile();
        }
        return;
      }
      if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
        scheduleReconnect(entry);
      }
    });

  entry.channel = channel;
  realtimeChannelsCountDelta(1);
}

/**
 * Canal único por workspace (RLS + filter workspace_id).
 * Fan-out interno para ConversationList, header e sidebar.
 */
export function subscribeWorkspaceConversationChanges(
  workspaceId: string,
  handler: ConversationChangeHandler,
): () => void {
  installVisibilityListener();
  installWatchdog();

  const entry = ensureHub(workspaceId);
  entry.handlers.add(handler);

  if (entry.reconnectTimer) {
    clearTimeout(entry.reconnectTimer);
    entry.reconnectTimer = null;
    entry.backoffMs = MIN_BACKOFF_MS;
  }

  if (
    REALTIME_PAUSE_ON_HIDDEN_ENABLED &&
    typeof document !== "undefined" &&
    !document.hidden &&
    hubPaused
  ) {
    resumeAllHubs();
  } else if (!isHubEffectivelyPaused()) {
    subscribeHub(entry);
  }

  return () => {
    entry.handlers.delete(handler);
    if (entry.handlers.size === 0) {
      if (entry.reconnectTimer) {
        clearTimeout(entry.reconnectTimer);
        entry.reconnectTimer = null;
      }
      teardownChannel(entry);
      hubs.delete(workspaceId);
    }
  };
}
